import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { axiosPrivate } from 'app/utils';

import {
  organisationFeatureFlagsQueryKey,
  type FeatureFlagKey,
  type GetOrganisationFeatureFlagsResponse,
} from '../../../hooks/useOrganisationFeatureFlags';

interface UpdateOrganisationFeatureFlagsParams {
  organisationId: string;
  flag: FeatureFlagKey;
  value: boolean;
}

function updateOrganisationFeatureFlags({
  organisationId,
  flag,
  value,
}: UpdateOrganisationFeatureFlagsParams) {
  return axiosPrivate
    .patch(`/v3/pharmacist/organisations/${organisationId}/feature-flags`, { [flag]: value })
    .then((r) => r.data);
}

export function useUpdateOrganisationFeatureFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrganisationFeatureFlags,
    // Optimistic write with rollback on error: the toggle should reflect the
    // new value immediately rather than waiting for the PATCH round-trip.
    onMutate: async ({ organisationId, flag, value }: UpdateOrganisationFeatureFlagsParams) => {
      const queryKey = organisationFeatureFlagsQueryKey(organisationId);
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<AxiosResponse<GetOrganisationFeatureFlagsResponse>>(queryKey);

      queryClient.setQueryData<AxiosResponse<GetOrganisationFeatureFlagsResponse>>(
        queryKey,
        (old) =>
          old
            ? {
                ...old,
                data: {
                  ...old.data,
                  featureFlags: { ...old.data.featureFlags, [flag]: value },
                },
              }
            : old
      );

      return { previous, queryKey };
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    meta: {
      successMessage: 'Builder settings updated successfully',
      errorMessage: 'Failed to update builder settings',
    },
  });
}
